/**
 * The banner stub's avatar stack (EN-11155 Details): three to six avatars in
 * two staggered rows, the larger row in front and the other raised half a
 * step behind it; two stay in one row. d = circle, v = how far the front row
 * sits below the back row, p = step along a row. Whole pixels throughout.
 */
export type BannerHeight = 64 | 72 | 80;

type Stack = { d: number; v: number; p: number };

const STACK: Record<BannerHeight, Record<3 | 4 | 5 | 6, Stack>> = {
  72: {
    3: { d: 22, v: 11, p: 30 },
    4: { d: 21, v: 12, p: 28 },
    5: { d: 20, v: 13, p: 22 },
    6: { d: 19, v: 14, p: 16 }
  },
  80: {
    3: { d: 23, v: 12, p: 32 },
    4: { d: 22, v: 13, p: 29 },
    5: { d: 21, v: 14, p: 22 },
    6: { d: 20, v: 15, p: 16 }
  },
  64: {
    3: { d: 17, v: 9, p: 24 },
    4: { d: 17, v: 9, p: 24 },
    5: { d: 17, v: 9, p: 24 },
    6: { d: 16, v: 10, p: 18 }
  }
};

const ROW: Record<BannerHeight, { one: number; two: number; overlap: number }> = {
  72: { one: 28, two: 26, overlap: 6 },
  80: { one: 28, two: 26, overlap: 6 },
  64: { one: 24, two: 22, overlap: 5 }
};

/** The player's avatar (frame outside it) and the freeze medallion, per height. */
export const STUB_VISUAL: Record<BannerHeight, { avatar: number; freeze: number }> = {
  72: { avatar: 22, freeze: 24 },
  80: { avatar: 22, freeze: 24 },
  64: { avatar: 18, freeze: 20 }
};

export interface StackDot {
  x: number;
  y: number;
  d: number;
  /** The art is drawn at 106% behind the circle so transparent margins never show. */
  big: number;
  crop: number;
}

const fill = (x: number, y: number, d: number): StackDot => {
  const big = +(d * 1.06).toFixed(2);
  return { x, y, d, big, crop: +((big - d) / 2).toFixed(2) };
};

export function avatarStack(
  count: number,
  height: BannerHeight
): { width: number; height: number; dots: StackDot[] } {
  const n = Math.max(1, Math.min(6, count));
  if (n <= 2) {
    const r = ROW[height];
    const d = n === 1 ? r.one : r.two;
    const step = d - r.overlap;
    const dots = Array.from({ length: n }, (_, i) => fill(i * step, 0, d));
    return { width: n * d - (n - 1) * r.overlap, height: d, dots };
  }
  const k = STACK[height][n as 3 | 4 | 5 | 6];
  const back = Math.floor(n / 2);
  // The first avatars sit in the back row, the rest (the set's character last) in front.
  const dots = Array.from({ length: n }, (_, i) => {
    const isBack = i < back;
    const j = isBack ? i : i - back;
    return fill(Math.round(j * k.p + (isBack ? k.p / 2 : 0)), isBack ? 0 : k.v, k.d);
  });
  const width = Math.max(...dots.map((o) => o.x)) + k.d;
  return { width, height: k.d + k.v, dots };
}
